import styles from './Tab.module.scss';
import Box from '@components/Box';
import NotificationDetailsView from '../components/NotificationDetailsView';
import NotificationItem from '../components/NotificationItem';
import {
  getNotifications,
  getNotificationsPageByLinkCode,
  INotificationItem,
  TPagedNotificationsResult
} from '@api/apiClient/notifications';
import { Loader } from '@components/Loader';
import { NotificationsTabProps, NotificationsCache } from '../types';
import { Text, TextColor } from '@components/Text';
import { ToggleButtons, ToggleButtonTypes } from '@components/ToggleButtons';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfile } from '@contexts/ProfileContext';

const PAGE_SIZE = 20;

const NotificationsTab: React.FC<NotificationsTabProps> = (props: NotificationsTabProps): React.ReactElement => {
  const { category, refetchGetNotificationsUnreadCount } = props;

  const [activeId, setActiveId] = useState<string>('');
  const [filter, setFilter] = useState<string>('all');
  const [searchParams] = useSearchParams();
  const { account } = useProfile();
  const currentLanguage = account!.region;
  const defaultQueryParams = { page: { page: 1, pageSize: PAGE_SIZE } };
  const handleObserver = useRef<IntersectionObserver | null>(null);
  const newsId = searchParams.get('newsId');
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const notificationQueryKey = ['getNotifications', category, filter];

  const {
    data: notificationsData,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage
  } = useInfiniteQuery({
    queryKey: notificationQueryKey,
    queryFn: ({ pageParam }) => getNotifications({
      ...(pageParam ?? defaultQueryParams),
      filters: [
        { name: 'Language', value: currentLanguage },
        ...((category !== 'Unknown' && category !== 'All') ? [{ name: 'Category', value: category }] : []),
        ...(filter === 'unread' ? [{ name: 'IsRead', value: 'false' }] : []),
        ...(filter === 'favorites' ? [{ name: 'IsFavorite', value: 'true' }] : [])
      ]
    }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.total <= (pages.length * PAGE_SIZE)) return undefined;
      return { page: { page: pages.length + 1, pageSize: PAGE_SIZE } };
    },
    retry: 1,
    enabled: !newsId
  });

  const { mutate: mutateGetNotificationsPageByLinkCode } = useMutation({
    mutationFn: getNotificationsPageByLinkCode,
    onSuccess: async (result: TPagedNotificationsResult) => {
      const pageNumber = result.pageNumber + 1;
      const pageData = await getNotifications({
        page: { page: pageNumber, pageSize: PAGE_SIZE },
        filters: [
          { name: 'Language', value: currentLanguage },
          ...(category !== 'Unknown' ? [{ name: 'Category', value: category }] : [])
        ]
      });
      queryClient.setQueryData(notificationQueryKey, (oldData: any) => {
        if (oldData) {
          const pageIndex = oldData.pageParams.findIndex(
              (p: any) => p?.page?.page === pageNumber
          );
          let newPages;
          let newPageParams;
          if (pageIndex !== -1) {
            newPages = oldData.pages.map((p: any, idx: number) =>
                idx === pageIndex ? pageData : p
            );
            newPageParams = [...oldData.pageParams];
          } else {
            newPages = [...oldData.pages, pageData];
            newPageParams = [
              ...oldData.pageParams,
              { page: { page: pageNumber, pageSize: PAGE_SIZE } }
            ];
          }
          return {
            ...oldData,
            pages: newPages,
            pageParams: newPageParams
          };
        } else {
          return {
            pageParams: [{ page: { page: pageNumber, pageSize: PAGE_SIZE } }],
            pages: [pageData]
          };
        }
      });
      setActiveId(result.notificationId);
      setFilter('all');
      setTimeout(() => {
        const element = document.getElementById(`notification-${result.notificationId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  });

  useEffect(() => {
    if (newsId) {
      mutateGetNotificationsPageByLinkCode({ linkCode: newsId, pageSize: PAGE_SIZE });
    }
  }, [newsId]);

  const updateItem = (data: INotificationItem): void => {
    const prevData: NotificationsCache | undefined = queryClient.getQueryData(notificationQueryKey);
    const newData = prevData && {
      ...prevData,
      pages: prevData.pages.map(p => ({
        ...p,
        items: p.items.map(item => (item.id === data.id ? data : item))
      }))
    };
    queryClient.setQueryData(notificationQueryKey, newData);
  };

  const lastElement: React.LegacyRef<HTMLDivElement> = useCallback(
      (element: HTMLDivElement) => {
        if (isFetching) return;
        if (handleObserver.current) handleObserver.current.disconnect();
        handleObserver.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting && hasNextPage && !isFetching) {
            fetchNextPage();
          }
        });
        if (element) handleObserver.current.observe(element);
      },
      [isLoading, hasNextPage]
  );

  const notificationItems = (notificationsData?.pages || []).reduce(
      (acc, item) => [...acc, ...item.items], [] as INotificationItem[]
  );

  return (
      <div className={styles.root}>
        <div className={styles.leftPanel}>
          <div className={styles.filtersPanel}>
            <div>
              <ToggleButtons
                  value={filter}
                  onChange={setFilter}
                  items={[
                    { id: 'all', title: t('notificationsPage.filtersAll') },
                    { id: 'unread', title: t('notificationsPage.filtersUnread') },
                    { id: 'favorites', title: t('notificationsPage.filtersFavorites') }
                  ]}
                  type={ToggleButtonTypes.ROUNDED_BLOCK}
              />
            </div>
          </div>
          {(!notificationItems?.length && (isLoading || isFetching)) ? (
              <Box p={5}>
                <Loader
                    dark
                    size="xs"
                />
              </Box>
          ) : (
              notificationItems?.length ? (
                  <div className={styles.notificationsList}>
                    {notificationItems.map((item: INotificationItem, idx: number) => (
                        <div
                            id={`notification-${item.id}`}
                            key={item.id}
                        >
                          {hasNextPage && (idx === notificationItems?.length - 1) && (
                              <div ref={lastElement}/>
                          )}
                          <NotificationItem
                              data={item}
                              isActive={item.id === activeId}
                              onClick={() => setActiveId(item.id)}
                              updateItem={updateItem}
                          />
                        </div>
                    ))}
                    {isFetching ? (
                        <Box p={5}>
                          <Loader
                              dark
                              size="xs"
                          />
                        </Box>
                    ) : null}
                  </div>
              ) : (
                  <Box
                      p={5}
                      textAlign="center"
                  >
                    <Text color={TextColor.DARK_MUTED}>
                      {t('notificationsPage.noNotifications')}
                    </Text>
                  </Box>
              )
          )}
        </div>
        <div className={styles.rightPanel}>
          <NotificationDetailsView
              handlerUpdateItem={updateItem}
              id={activeId}
              isListLoading={isLoading}
              isListEmpty={!notificationItems?.length}
              refetchGetNotificationsUnreadCount={refetchGetNotificationsUnreadCount}
          />
        </div>
      </div>
  );
};

export default NotificationsTab;
